import React, { useState, useEffect } from 'react';
import { X, FileText, FileSpreadsheet, File } from 'lucide-react';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const DownloadModal = ({ isOpen, onClose, data, columns, tableName }) => {
  const [filename, setFilename] = useState('');
  const [fileType, setFileType] = useState('csv');

  // Generate default filename when modal opens
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const yy = String(now.getFullYear()).slice(-2);
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      setFilename(`${tableName}_${yy}${mm}${dd}-${hh}${min}`);
      setFileType('csv');
    }
  }, [isOpen, tableName]);

  if (!isOpen) return null;

  // Get file extension for current type
  const getExtension = () => {
    switch (fileType) {
      case 'csv': return '.csv';
      case 'excel': return '.xlsx';
      case 'pdf': return '.pdf';
      default: return '';
    }
  };

  // Get MIME type for current file type
  const getMimeType = () => {
    switch (fileType) {
      case 'csv': return 'text/csv';
      case 'excel': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'pdf': return 'application/pdf';
      default: return '';
    }
  };

  // Save file using File System Access API or fallback to download
  const saveFile = async (blob, suggestedName) => {
    // Try File System Access API (Chrome/Edge)
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          startIn: 'downloads',
          types: [{
            description: fileType.toUpperCase() + ' File',
            accept: { [getMimeType()]: [getExtension()] }
          }]
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (err) {
        // User cancelled or API failed, fall through to download
        if (err.name === 'AbortError') {
          return; // User cancelled, don't download
        }
      }
    }

    // Fallback: traditional download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = suggestedName;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleDownload = async () => {
    if (fileType === 'csv') {
      await exportToCSV();
    } else if (fileType === 'excel') {
      await exportToExcel();
    } else if (fileType === 'pdf') {
      await exportToPDF();
    }
    onClose();
  };

  const exportToCSV = async () => {
    const csv = [
      columns.map(c => c.label).join(','),
      ...data.map(row =>
        columns.map(c => {
          const v = String(row[c.key] || '');
          return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    await saveFile(blob, `${filename}.csv`);
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Data');

    // Add header row
    const headerRow = worksheet.addRow(columns.map(c => c.label));

    // Style header row - theme blue background (#1a5568), white text, frozen
    headerRow.eachCell(cell => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1A5568' } // Theme primary color
      };
      cell.font = {
        color: { argb: 'FFFFFFFF' },
        bold: true
      };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
    });

    // Add data rows
    data.forEach(row => {
      worksheet.addRow(columns.map(c => row[c.key] || ''));
    });

    // Auto-size columns based on content
    worksheet.columns.forEach((column, i) => {
      const header = columns[i]?.label || '';
      let maxLength = header.length;

      // Check data lengths
      data.forEach(row => {
        const cellValue = String(row[columns[i]?.key] || '');
        if (cellValue.length > maxLength) {
          maxLength = cellValue.length;
        }
      });

      // Set width with padding, max 50
      column.width = Math.min(maxLength + 2, 50);
    });

    // Freeze header row
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];

    // Generate blob and save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    await saveFile(blob, `${filename}.xlsx`);
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [columns.map(c => c.label)],
      body: data.map(row => columns.map(c => row[c.key] || '')),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [26, 85, 104] } // Theme primary #1a5568
    });

    const blob = doc.output('blob');
    await saveFile(blob, `${filename}.pdf`);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content download-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Download Data</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label>Filename</label>
            <input
              type="text"
              value={filename}
              onChange={e => setFilename(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>File Type</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="fileType"
                  value="csv"
                  checked={fileType === 'csv'}
                  onChange={e => setFileType(e.target.value)}
                />
                <FileText size={16} />
                <span>CSV</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="fileType"
                  value="excel"
                  checked={fileType === 'excel'}
                  onChange={e => setFileType(e.target.value)}
                />
                <FileSpreadsheet size={16} />
                <span>Excel</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="fileType"
                  value="pdf"
                  checked={fileType === 'pdf'}
                  onChange={e => setFileType(e.target.value)}
                />
                <File size={16} />
                <span>PDF</span>
              </label>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleDownload}>
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;
